import { useState, useEffect } from "react";
import { Accordion } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { ReactComponent as NoResults } from "assets/images/iconsV2/search-document.svg";
import { ReactComponent as EmptyBox } from "assets/images/iconsV2/empty-box.svg";
import { getConsolidatedOrders } from "api";
import FormSection from "components/v2/FormSection/FormSection";
import FormHeader from "components/v2/FormSection/components/FormHeader/FormHeader";
import EmptyState from "components/v2/EmptyState/EmptyState";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import errorToast from "helpers/errorToast";
import Tablefooter from "components/v2/Tablefooter/Tablefooter";
import ConsolidatedOrderTable from "./components/ConsolidatedOrderTable/ConsolidatedOrderTable";
import Footer from "./components/Footer/Footer";
import FilterRow from "./components/FilterRow/FilterRow";
import { filterData, filterSearchData } from "./helpers/filterData";

import "./OrderSelection.scss";

type OrderSelectionProps = {
  selectedOrders: [];
  setSelectedOrders: Function;
  onNextClick: () => void;
  isEdit?: boolean;
};

function OrderSelection({ selectedOrders, setSelectedOrders, onNextClick, isEdit }: OrderSelectionProps) {
  const { watch } = useFormContext();
  const [timeFrame, setTimeFrame] = useState(3);
  const [errorState, setErrorState] = useState(false);
  const [consolidatedOrders, setConsolidatedOrders] = useState([]);
  const [filteredConsolidatedOrders, setFilteredConsolidatedOrders] = useState<any[]>([]);
  const [selectedTableId, setSelectedTableId] = useState("");
  const [activeAccordion, setActiveAccordion] = useState("");
  const [currentFilters, setCurrentFilters] = useState({});
  const [searchInput, setSearchInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [previousTableId, setPreviousTableId] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const availableQuickRequests = watch("available_quick_requests") || [];
  const origin = watch("schedule.pickup_fa") || {};
  const destination = watch("schedule.delivery_fa") || {};

  useEffect(() => {
    let filteredData: any = consolidatedOrders;

    if (currentFilters && Object.keys(currentFilters).length > 0)
      filteredData = filterData(consolidatedOrders, currentFilters);

    if (searchInput) filteredData = filterSearchData(filteredData, searchInput);

    setFilteredConsolidatedOrders(filteredData);
    setCurrentPage(1);
  }, [currentFilters, searchInput, consolidatedOrders]);

  useEffect(() => {
    const getAllOrders = (params = {}) => {
      setIsLoading(true);
      getConsolidatedOrders({ params })
        .then((res: any) => {
          setConsolidatedOrders(res.data);
          setFilteredConsolidatedOrders(res.data);
          if (res.data.length > 0) {
            setActiveAccordion(res.data[0].id);
          }
          if (errorState) setErrorState(false);
        })
        .catch((err) => {
          setErrorState(true);
          errorToast(err);
        })
        .finally(() => setIsLoading(false));
    };

    // send selected-orders params for edit case
    if (isEdit && availableQuickRequests.length > 0) {
      getAllOrders({
        "selected-orders": availableQuickRequests.join(","),
        origin,
        destination,
        timeframe: timeFrame,
      });
    } else if (!isEdit) {
      getAllOrders({ timeframe: timeFrame });
    }
  }, [isEdit && availableQuickRequests, timeFrame]);

  if (errorState)
    return (
      <div className="consolidation-orders-error">
        <EmptyState />
      </div>
    );

  // Calculate total pages based on filtered data
  const totalData = filteredConsolidatedOrders.length;
  const totalPages = Math.ceil(totalData / itemsPerPage);

  // Calculate the current data slice for the table
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentOrders = filteredConsolidatedOrders.slice(startIndex, startIndex + itemsPerPage);

  return (
    <>
      <div className="display-sm font-semibold mb-3xl">Select orders for consolidation</div>
      <div className="order-selection-container">
        <FormSection width="lg">
          <FormHeader
            title="Orders"
            description="Only orders that have not yet been shipped or assigned to a quote request will show here"
          />
          <div className="order-selection-container__filters">
            <FilterRow
              data={consolidatedOrders}
              currentFilters={currentFilters}
              setCurrentFilters={setCurrentFilters}
              searchInput={searchInput}
              setSearchInput={setSearchInput}
              setTimeFrame={setTimeFrame}
              timeFrame={timeFrame}
            />
          </div>
          <div className="order-selection-container__table-container">
            {isLoading && <LoadingSkeleton rows={2} bodyClasses="p-0" className="p-xl" noShadow />}
            {!isLoading && consolidatedOrders.length === 0 && (
              <div className="empty-state">
                <EmptyState
                  title="No orders available for consolidation"
                  description="You do not have any orders that have the same origin and destination."
                  customIcon={<EmptyBox />}
                  noActions
                />
              </div>
            )}
            {!isLoading && !searchInput && consolidatedOrders.length > 0 && filteredConsolidatedOrders.length === 0 && (
              <div className="empty-state">
                <EmptyState
                  title="No orders available for consolidation"
                  description="Try removing or adjusting your filters"
                  customIcon={<EmptyBox />}
                  noActions
                />
              </div>
            )}
            {/* If there are available orders, but no orders after using searchInput, show no search result screen */}
            {!isLoading && consolidatedOrders.length > 0 && searchInput && filteredConsolidatedOrders.length === 0 && (
              <div className="empty-state">
                <EmptyState
                  title="No search results"
                  description={`Your search “${searchInput}” did not match any orders. Please try again.`}
                  customIcon={<NoResults />}
                  noActions
                />
              </div>
            )}
            {currentOrders.length > 0 && !isLoading && (
              <div className="consolidation-orders-container__table-section">
                <Accordion activeKey={activeAccordion}>
                  {currentOrders.map((order) => {
                    if (!order.orders.length) return null;
                    return (
                      <ConsolidatedOrderTable
                        order={order}
                        tableId={order.id}
                        selectedOrders={selectedOrders}
                        setSelectedOrders={setSelectedOrders}
                        setActiveAccordion={setActiveAccordion}
                        selectedTableId={selectedTableId}
                        setSelectedTableId={setSelectedTableId}
                        activeAccordion={activeAccordion}
                        previousTableId={previousTableId}
                        setPreviousTableId={setPreviousTableId}
                        filteredData={currentOrders}
                      />
                    );
                  })}
                </Accordion>
              </div>
            )}
          </div>
          <Tablefooter
            totalPages={totalPages}
            totalData={totalData}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            pageClicked={(page) => setCurrentPage(page)}
          />
          {selectedOrders.length > 0 && <Footer onNextClick={onNextClick} selectedOrders={selectedOrders} />}
        </FormSection>
      </div>
    </>
  );
}

OrderSelection.defaultProps = {
  isEdit: false,
};

export default OrderSelection;
